import React, { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { Clear } from "@mui/icons-material"
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputBaseProps,
  OutlinedInput,
  Stack,
  Typography
} from "@mui/material"
import { scrollbarMixin } from "src/providers/AppProviders"
import { useResponsive } from "src/utils/useResponsive"

interface CustomTextareaProps extends InputBaseProps {
  value: string | undefined
  clearable?: boolean
  placeholder?: string
  onUpdate?: (v: string) => void
  onErrorChange?: (active: boolean) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLSpanElement>) => void
  endBottomAdornment?: ReactNode
  maxInputLength?: number
  inputRef?: React.Ref<HTMLTextAreaElement | HTMLInputElement>
  onFocus?: () => void
}

const CustomTextarea = (props: CustomTextareaProps) => {
  const {
    value,
    disabled,
    clearable,
    onUpdate,
    onErrorChange,
    placeholder = "",
    rows,
    onKeyDown,
    endBottomAdornment,
    maxInputLength = 1500,
    inputRef,
    onFocus,
    ...restProps
  } = props
  const [errorMsg, setErrorMsg] = useState("")

  const { t } = useTranslation("common")
  const { isMobile } = useResponsive()

  const prevent = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault()
    return false
  }

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value
    onUpdate && onUpdate(v)
    if (v.length > maxInputLength) {
      setErrorMsg(t("inputLimit", { val: maxInputLength }))
      onErrorChange && onErrorChange(true)
    } else {
      setErrorMsg("")
      onErrorChange && onErrorChange(false)
    }
  }

  const clearForm = () => {
    onUpdate && onUpdate("")
    setErrorMsg("")
    onErrorChange && onErrorChange(false)
  }

  const showClearButton = value && clearable

  return (
    <FormControl fullWidth>
      <OutlinedInput
        multiline
        rows={rows}
        fullWidth
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => onFocus?.()}
        onChange={onChange}
        onContextMenu={prevent}
        onKeyDown={onKeyDown}
        error={!!errorMsg}
        inputRef={inputRef}
        data-testid="custom-textarea-input"
        endAdornment={
          <Stack
            justifyContent={showClearButton ? "space-between" : "end"}
            direction={isMobile ? "row" : "column"}
            sx={{
              height: { xs: "auto", md: clearable ? "calc(100% - 24px)" : "calc(100% - 16px)" },
              position: "absolute",
              right: "16px",
              bottom: "16px",
              alignItems: { xs: "center", md: "flex-end" },
              zIndex: 10
            }}
          >
            {endBottomAdornment || (
              <Typography variant="body2" data-testid="textarea-character-count" sx={{ order: { xs: 0, md: 1 } }}>
                {value?.length || 0}/{maxInputLength}
              </Typography>
            )}
            {showClearButton && (
              <IconButton
                onClick={clearForm}
                onMouseDown={clearForm}
                size="small"
                sx={{ width: "fit-content" }}
                data-testid="clear-textarea-button"
              >
                <Clear fontSize="large" />
              </IconButton>
            )}
          </Stack>
        }
        sx={{
          pt: 1,
          pb: 6,
          ...(isMobile && {
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none"
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none"
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none"
            }
          })
        }}
        inputProps={{
          sx: {
            zIndex: 10
          }
        }}
        {...restProps}
        value={value}
      />
      {errorMsg && (
        <FormHelperText error={!!errorMsg} data-testid="textarea-error-message">
          {errorMsg}
        </FormHelperText>
      )}
    </FormControl>
  )
}
export default CustomTextarea
